import { useEffect, useState } from 'react';
import { array, option } from 'fp-ts';
import { constNull, pipe } from 'fp-ts/function';
import { Button, IconName, Table, TableHeader, TableProvider, useTableState } from '@optimus-web/core';
import { LocalizedText, useAppClockService, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag, RouteName } from '@mews-commander/enums';
import { Payout } from '@mews-commander/data-access';
import { replace, useResolveRoute } from '@mews/routing';
import { createUUID } from '@mews/std';
import { setUrlParams, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getFilters } from '../selectors';
import { filtersSubmit, setFilters } from '../actions/filters';
import { navigateInModal } from '../../app/actions/common';
import { CapitalBannerForPayoutReport } from '../../financialServicesCapital/CapitalBannerForPayoutReport';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '../constants';
import { useExportPayouts, useGetOpenPayouts, useGetPayouts, useGetPayoutSummaries } from '../queries';
import { FilterDateValues } from '../types';
import { hasPayoutSummaryMultipleStateAmounts, getPayoutsTableData, getUrlFilters } from '../utils';
import { getPayoutReportColumns } from './tableMapping';
import { PayoutSummaryCard } from './PayoutSummaryCard';
import { PayoutReportSidebar } from './PayoutReportSidebar';
import { PayoutReportHeader } from './PayoutReportHeader';
import { PayoutReportLayout } from './PayoutReportLayout';
import { PayoutSummaryGrid } from './PayoutSummaryGrid';
import { useCreateScheduledExportDialogForm } from './scheduledExport/useCreateScheduledExportDialogForm';

export const PayoutReport = ({ guideLink }: { guideLink: string }) => {
    const dispatch = useCommanderThunkDispatch();
    const resolveRoute = useResolveRoute();
    const appClockService = useAppClockService();
    const isPrequalifiedUser = useFeatureFlag(FeatureFlag.financialServicesCapitalPrequalifiedUsersEnabled);

    const { startUtc, endUtc } = useCommanderSelector((state) => getFilters(state, { appClockService }));

    const [tableState, setTableState] = useTableState<Payout>({
        pagination: {
            pageSize: DEFAULT_PAGE_SIZE,
            pageIndex: DEFAULT_PAGE_INDEX,
        },
    });

    const [isDialogOpened, setIsDialogOpened] = useState(false);
    const openDialog = () => setIsDialogOpened(true);
    const closeDialog = () => setIsDialogOpened(false);

    const {
        data: payouts = { data: [], totalRowCount: 0 },
        isFetching: isPayoutDataFetching,
        isInitialLoading: isPayoutDataInitialLoading,
        isError: hasPayoutDataFailed,
    } = useGetPayouts({
        pagination: {
            ...tableState.pagination,
        },
    });

    useEffect(() => {
        if (isPayoutDataInitialLoading) {
            dispatch(setFilters(getUrlFilters()));
        }
    }, [dispatch, isPayoutDataInitialLoading]);

    const {
        data: { payoutSummaries } = { payoutSummaries: [] },
        isInitialLoading: isPayoutSummariesDataFetching,
        isError: hasPayoutSummariesDataFailed,
    } = useGetPayoutSummaries();

    const {
        data: { openPayouts } = { openPayouts: [] },
        isFetching: isOpenPayoutsDataFetching,
        isError: hasOpenPayoutsDataFailed,
    } = useGetOpenPayouts();

    useEffect(() => {
        if (!startUtc || !endUtc) {
            return;
        }
        dispatch(replace(setUrlParams({ startUtc, endUtc })));
    }, [dispatch, startUtc, endUtc]);

    const exportPayouts = useExportPayouts();

    const redirectToPayoutDetail = (id: string) => {
        dispatch(
            navigateInModal(
                resolveRoute(RouteName.PayoutReportDetail, {
                    id,
                    query: { startUtc, endUtc },
                })
            )
        );
    };

    const redirectToOpenPayout = (currencyCode: string) => {
        dispatch(
            navigateInModal(
                resolveRoute(RouteName.OpenPayoutDetail, {
                    currencyCode,
                    query: { startUtc, endUtc },
                })
            )
        );
    };

    const resetPagination = () => {
        setTableState({
            ...tableState,
            pagination: { ...tableState.pagination, pageIndex: DEFAULT_PAGE_INDEX },
        });
    };

    const handleFiltersSubmit = (values: FilterDateValues) => {
        dispatch(filtersSubmit(values));
        resetPagination();
        closeDialog();
    };

    const handleExport = () => {
        resetPagination();
        exportPayouts.mutate({ pagination: { ...tableState.pagination } });
        closeDialog();
    };

    const [renderDialog, toggleDialog] = useCreateScheduledExportDialogForm();

    const handleScheduleExport = () => toggleDialog();

    const columns = getPayoutReportColumns({ handleLinkClick: redirectToPayoutDetail });

    const payoutSummaryCards = pipe(
        openPayouts,
        array.map((openPayout) => ({
            openPayout,
            payoutSummary: pipe(
                payoutSummaries,
                array.findFirst((payoutSummary) => payoutSummary.totalNet.currencyCode === openPayout.net.currencyCode),
                option.getOrElseW(constNull)
            ),
        }))
    );

    const hasBannerTitle = hasPayoutSummaryMultipleStateAmounts(payoutSummaries);

    return (
        <>
            <PayoutReportLayout
                exportHandler={handleExport}
                scheduleExportHandler={handleScheduleExport}
                title={<PayoutReportHeader />}
                hasFailed={hasPayoutDataFailed || hasPayoutSummariesDataFailed || hasOpenPayoutsDataFailed}
                isFetching={isPayoutSummariesDataFetching || isOpenPayoutsDataFetching}
                guideLink={guideLink}
                summary={
                    <PayoutSummaryGrid>
                        {payoutSummaryCards.map(({ openPayout, payoutSummary }) => (
                            <PayoutSummaryCard
                                key={createUUID()}
                                payoutSummary={payoutSummary}
                                openPayout={openPayout}
                                redirectToOpenPayout={redirectToOpenPayout}
                            />
                        ))}
                        {isPrequalifiedUser && <CapitalBannerForPayoutReport hasBannerTitle={hasBannerTitle} />}
                    </PayoutSummaryGrid>
                }
            >
                <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
                    <TableHeader
                        title={<LocalizedText textKey={TextKey.PayoutPlural} />}
                        rightSide={
                            <Button icon={IconName.Filter} onClick={openDialog} type="submit">
                                <LocalizedText textKey={TextKey.Filter} />
                            </Button>
                        }
                    />
                    <Table
                        data={getPayoutsTableData(payouts.data)}
                        uniqueRowId="id"
                        loading={isPayoutDataFetching}
                        amountOfRows={payouts.totalRowCount}
                        emptyState={{
                            primaryText: <LocalizedText textKey={TextKey.PayoutReportEmptyStatePrimary} />,
                            secondaryText: <LocalizedText textKey={TextKey.PayoutReportEmptyStateSecondary} />,
                        }}
                    />
                </TableProvider>
            </PayoutReportLayout>
            <PayoutReportSidebar
                active={isDialogOpened}
                closeDialog={closeDialog}
                onFiltersSubmit={handleFiltersSubmit}
            />
            {renderDialog()}
        </>
    );
};
